import { getUserSessionStore, hasUserSessionStore, } from '@zillow/user-session-store-core';
import * as actions from './constants/actions';
/**
 * Forwards dispatch to global store if it exists.
 * @param action - object describing change to be made to store
 * @return dispatched action
 */
export default function dispatchToUserSessionStore(action) {
    if (hasUserSessionStore()) {
        return getUserSessionStore().dispatch(action);
    }
    // eslint-disable-next-line no-console
    console.error('UserSession store is undefined');
    return null;
}
/**d
 * Curry for dispatchToUserSessionStore.
 * @param type - action type
 * curried dispatch function
 */
function dispatchOfType(type) {
    return (payload) => dispatchToUserSessionStore({
        type,
        ...payload,
    });
}
/**
 * Sets user.authToken.
 * @param authToken - session authToken
 * @return SET_USER_AUTH_TOKEN action
 */
export function setUserAuthToken(authToken) {
    if (typeof authToken === 'undefined') {
        throw new Error('no auth token specified');
    }
    return dispatchOfType(actions.SET_USER_AUTH_TOKEN)({ authToken });
}
/**
 * Sets user.loggedIn to true.
 * @return SET_USER_LOGGED_IN action
 */
export function setUserLoggedIn() {
    return dispatchOfType(actions.SET_USER_LOGGED_IN)({});
}
/**
 * Sets user.loggedIn to false.
 * @return SET_USER_LOGGED_OUT action
 */
export function setUserLoggedOut() {
    return dispatchOfType(actions.SET_USER_LOGGED_OUT)({});
}
/**
 * Sets user.name to whatever it is passed.
 * @return SET_USER_NAME action
 */
export function setUserName(name) {
    return dispatchOfType(actions.SET_USER_NAME)({ name });
}
/**
 * Sets regLogin.inputValues.
 * @param inputValues - reg-login form input values
 * @return SET_REG_LOGIN_INPUT_VALUES action
 */
export function setRegLoginInputValues(inputValues) {
    if (typeof inputValues === 'undefined') {
        throw new Error('no input values specified');
    }
    return dispatchOfType(actions.SET_REG_LOGIN_INPUT_VALUES)({ inputValues });
}
/**
 * Sets regLogin.node.
 * @param node  - reg-login node
 * @return SET_REG_LOGIN_NODE action
 */
export function setRegLoginNode(node) {
    return dispatchOfType(actions.SET_REG_LOGIN_NODE)({ node });
}
/**
 * Sets regLogin.path.
 * @param path - reg-login route path
 * @return SET_REG_LOGIN_PATH action
 */
export function setRegLoginPath(path) {
    if (typeof path === 'undefined') {
        throw new Error('no path specified');
    }
    return dispatchOfType(actions.SET_REG_LOGIN_PATH)({ path });
}
/**
 * Sets regLogin.zmaHandlerFns.
 * @param zmaHandlerFns - reg-login callbacks. If set, triggers zma form after reg login
 * @return SET_REG_LOGIN_ZMA_HANDLERS action
 */
export function setRegLoginZmaHandlers(zmaHandlerFns) {
    if (zmaHandlerFns !== null && typeof zmaHandlerFns !== 'object') {
        throw new Error(`unexpected zmaHandlerFns type ${typeof zmaHandlerFns}`);
    }
    return dispatchOfType(actions.SET_REG_LOGIN_ZMA_HANDLERS)({ zmaHandlerFns });
}
/**
 * Sets regLogin.requiresPassword
 * @param requiresPassword - new value to set
 * @return SET_PASSWORD_REQUIRED action
 */
export function setRequiresPassword(requiresPassword) {
    // using !! to ensure passwordRequired is recorded as a boolean
    return dispatchOfType(actions.SET_PASSWORD_REQUIRED)({ requiresPassword: !!requiresPassword });
}
/**
 * Sets regLogin.resetPasswordLinkUrl
 * @param resetPasswordLinkUrl - new value to set
 * @return SET_RESET_PASSWORD_LINK_URL action
 */
export function setResetPasswordLinkUrl(resetPasswordLinkUrl) {
    if (resetPasswordLinkUrl !== null && typeof resetPasswordLinkUrl !== 'string') {
        throw new Error(`unexpected resetPasswordLinkUrl type ${typeof resetPasswordLinkUrl}`);
    }
    return dispatchOfType(actions.SET_RESET_PASSWORD_LINK_URL)({ resetPasswordLinkUrl });
}
/**
 * Sets regLogin.redirectUrl.
 * @param redirectUrl - reg-login redirectUrl for post submit success
 * @return SET_REG_LOGIN_REDIRECT_URL action
 */
export function setRegLoginRedirectUrl(redirectUrl) {
    if (typeof redirectUrl === 'undefined') {
        throw new Error('no redirect URL specified');
    }
    return dispatchOfType(actions.SET_REG_LOGIN_REDIRECT_URL)({ redirectUrl });
}
/**
 * Sets regLogin.uiConfig. If uiConfig is not provided values will be reset
 * to their default values
 * @param uiConfig - reg-login uiConfig
 * @return SET_REG_LOGIN_UI_CONFIG action
 */
export function setRegLoginUiConfig(uiConfig) {
    return dispatchOfType(actions.SET_REG_LOGIN_UI_CONFIG)({ uiConfig });
}
/**
 * Sets regLogin.uiConfig with the default values. uiConfig param will
 * override values
 * @param uiConfig - reg-login uiConfig
 * @return SET_REG_LOGIN_UI_CONFIG_DEFAULTS action
 */
export function setRegLoginUiConfigDefaults(uiConfig) {
    return dispatchOfType(actions.SET_REG_LOGIN_UI_CONFIG_DEFAULTS)({ uiConfig });
}
/**
 * Sets regLogin.showLightbox to true.
 * @return SHOW_REG_LOGIN_LIGHTBOX action
 */
export function showRegLoginLightbox() {
    return dispatchOfType(actions.SHOW_REG_LOGIN_LIGHTBOX)({});
}
/**
 * Sets regLogin.showLightbox to false.
 * @return HIDE_REG_LOGIN_LIGHTBOX action
 */
export function hideRegLoginLightbox() {
    return dispatchOfType(actions.HIDE_REG_LOGIN_LIGHTBOX)({});
}
/**
 * Sets regLogin.showGoogleOneTap to false.
 * @return {Object}
 */
export function hideGoogleOneTap() {
    return dispatchOfType(actions.HIDE_GOOGLE_ONE_TAP)({});
}
/**
 * Sets regLogin.showGoogleOneTap to true.
 * @return {Object}
 */
export function showGoogleOneTap() {
    return dispatchOfType(actions.SHOW_GOOGLE_ONE_TAP)({});
}
/**
 * Sets onboarding.showOnboardingQuestionnaire to true.
 * @return SHOW_ONBOARDING_QUESTIONNAIRE action
 */
export function showOnboardingQuestionnaire() {
    return dispatchOfType(actions.SHOW_ONBOARDING_QUESTIONNAIRE)({});
}
/**
 * Sets onboarding.showOnboardingQuestionnaire to false.
 * @return HIDE_ONBOARDING_QUESTIONNAIRE action
 */
export function hideOnboardingQuestionnaire() {
    return dispatchOfType(actions.HIDE_ONBOARDING_QUESTIONNAIRE)({});
}
/**
 * Sets fields in activation object/reducer. If config is not provided
 * values will be reset to their default values
 * @param {Object} Config - reg-login Config
 * @return SET_ONBOARDING_CONFIG action
 */
export function setOnboardingConfig(config) {
    return dispatchOfType(actions.SET_ONBOARDING_CONFIG)({ config });
}
